/**
 * /studio — Espace créateur (Phase 9).
 * Liste les apps du créateur, permet la création (from scratch ou depuis un concept)
 * et affiche le plan/quota.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { Sparkline } from '@/components/site/Sparkline';
import { useAuth } from '@/lib/auth';
import { useT, useLang } from '@/lib/i18n';
import { setPageMeta } from '@/lib/seo';
import { listMyApps, createApp, publishApp, unpublishApp, deleteApp, getAppActivity } from '@/lib/apps-api';
import { track } from '@/lib/analytics';
import { VERTICALS_BY_CODE } from '@/lib/verticals';
import { toast } from 'sonner';
import {
  Sparkles, Lock, ArrowRight, LayoutTemplate, Plus, Pencil,
  Globe, EyeOff, Trash2, ExternalLink, Loader2, AlertTriangle, Eye,
} from 'lucide-react';

const PLAN_ACCENT = { none: '#8892A6', basique: '#0EA5E9', pro: '#8B5CF6', illimite: '#D4A017' };

function formatDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso || ''; }
}

export default function Studio() {
  const t = useT();
  const { lang } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, items: [], plan: 'none', quota: 0, published_count: 0 });
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // app id

  useEffect(() => {
    setPageMeta({
      title: t('seo.studio_title'),
      description: t('seo.studio_description'),
      path: '/studio',
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang]);

  useEffect(() => {
    if (!loading && user === false) navigate('/connexion?next=/studio', { replace: true });
  }, [loading, user, navigate]);

  const reload = async () => {
    const res = await listMyApps();
    if (res.ok) {
      setState({ loading: false, items: res.items || [], plan: res.plan, quota: res.quota, published_count: res.published_count || 0 });
    } else {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    if (user) reload();
  }, [user]);

  if (loading || user === null) {
    return (
      <div data-testid="page-studio">
        <Navbar />
        <main className="min-h-[60vh] pt-[128px] md:pt-[168px]">
          <div className="om-container">
            <div className="text-sm text-[var(--om-muted-2)]" data-testid="studio-loading">…</div>
          </div>
        </main>
      </div>
    );
  }
  if (!user) return null;

  const plan = state.plan || 'none';
  const accent = PLAN_ACCENT[plan] || PLAN_ACCENT.none;
  const isUnlimited = plan === 'illimite';
  const quota = state.quota;
  const hasPlan = plan !== 'none';

  const handleCreate = async () => {
    if (creating) return;
    if (!hasPlan) {
      toast(t('studio.needs_plan'), { icon: '🔒' });
      navigate('/tarifs');
      return;
    }
    setCreating(true);
    const res = await createApp({ vertical: 'sante', lang });
    setCreating(false);
    if (res.ok) {
      track('app_created', { lang });
      navigate(`/studio/app/${res.app.id}`);
    } else {
      toast.error(t('builder.errors.create_failed'));
    }
  };

  const togglePublish = async (app) => {
    const action = app.status === 'published' ? unpublishApp : publishApp;
    const res = await action(app.id);
    if (res.ok) {
      if (app.status !== 'published') track('app_published', { lang });
      toast.success(app.status === 'published' ? t('studio.unpublished_toast') : t('studio.published_toast'));
      reload();
    } else {
      toast.error(res.detail || t('builder.errors.publish_failed'));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    const res = await deleteApp(confirmDelete);
    setConfirmDelete(null);
    if (res.ok) {
      toast.success(t('studio.deleted_toast'));
      reload();
    } else {
      toast.error(t('studio.delete_failed'));
    }
  };

  return (
    <div data-testid="page-studio">
      <Navbar />
      <main>
        <section className="pt-[128px] md:pt-[168px] pb-6">
          <div className="om-container">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <span className="om-eyebrow">{t('studio.eyebrow')}</span>
                <h1 className="om-h1 mt-4 text-[32px] sm:text-[40px] text-[var(--om-ink)]">
                  {t('studio.title', { email: user.email })}
                </h1>
                <p className="mt-2 max-w-lg text-sm leading-relaxed text-[var(--om-muted)]">
                  {t('studio.subtitle')}
                </p>
              </div>
              <div
                className="rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] px-5 py-4"
                data-testid="studio-plan-card"
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                  {t('studio.plan_label')}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider"
                    style={{ background: `${accent}18`, color: accent }}
                    data-testid="studio-plan-badge"
                  >
                    <Sparkles size={11} strokeWidth={2.6} /> {t(`studio.plan_${plan}`)}
                  </span>
                </div>
                {isUnlimited ? (
                  <div className="mt-2 text-xs text-[var(--om-muted)]" data-testid="studio-unlimited-hint">
                    <span className="font-bold text-[var(--om-ink)]">{t('studio.unlimited_apps')}</span>
                    <div className="mt-0.5 text-[11px] leading-relaxed text-[var(--om-muted-2)]">
                      {t('studio.unlimited_hint')}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-[var(--om-muted)]">
                    {t('studio.quota_label')}:{' '}
                    <span className="font-bold tabular-nums text-[var(--om-ink)]" data-testid="studio-quota">
                      {state.published_count}/{quota || '—'}
                    </span>{' '}
                    {quota > 1 ? t('studio.apps_plural') : t('studio.apps_singular')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="pb-24">
          <div className="om-container">
            {!hasPlan ? (
              <div
                className="relative overflow-hidden rounded-[28px] border border-dashed border-[var(--om-border)] bg-[var(--om-bg-pure)] p-10 text-center md:p-14"
                data-testid="studio-empty-noplan"
              >
                <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-[var(--om-ink)]/5">
                  <Lock size={20} strokeWidth={2.4} className="text-[var(--om-muted-2)]" />
                </div>
                <h2 className="text-2xl font-extrabold text-[var(--om-ink)]">{t('studio.noplan_title')}</h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[var(--om-muted)]">{t('studio.noplan_subtitle')}</p>
                <Link to="/tarifs" className="om-btn-primary mt-6" data-testid="studio-noplan-cta">
                  {t('studio.noplan_cta')} <ArrowRight size={14} strokeWidth={2.4} />
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-xl font-extrabold text-[var(--om-ink)]" data-testid="studio-apps-heading">
                    {t('studio.my_apps')}
                  </h2>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={creating}
                    className="om-btn-primary"
                    data-testid="studio-create-app-btn"
                  >
                    {creating ? <Loader2 size={14} strokeWidth={2.5} className="animate-spin" /> : <Plus size={14} strokeWidth={2.4} />}
                    {t('studio.create_app')}
                  </button>
                </div>

                {state.loading ? (
                  <div className="rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-10 text-center text-sm text-[var(--om-muted)]">…</div>
                ) : state.items.length === 0 ? (
                  <div className="rounded-[28px] border border-dashed border-[var(--om-border)] bg-[var(--om-bg-pure)] p-10 text-center md:p-14" data-testid="studio-empty-apps">
                    <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-[var(--om-ink)]/5">
                      <LayoutTemplate size={20} strokeWidth={2.4} className="text-[var(--om-muted-2)]" />
                    </div>
                    <h3 className="text-xl font-extrabold text-[var(--om-ink)]">{t('studio.empty_title')}</h3>
                    <p className="mx-auto mt-3 max-w-md text-sm text-[var(--om-muted)]">{t('studio.empty_subtitle')}</p>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                      <button onClick={handleCreate} className="om-btn-primary" data-testid="studio-empty-create-btn">
                        <Plus size={14} strokeWidth={2.4} /> {t('studio.create_app')}
                      </button>
                      <Link to="/generateur" className="om-btn-ghost" data-testid="studio-empty-generator-cta">
                        {t('studio.explore_generator')}
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {state.items.map((app) => (
                      <StudioAppCard
                        key={app.id}
                        app={app}
                        lang={lang}
                        t={t}
                        onToggle={togglePublish}
                        onDelete={() => setConfirmDelete(app.id)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
      {confirmDelete && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="studio-delete-dialog">
          <div className="w-full max-w-md rounded-3xl border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-6 shadow-xl">
            <h3 className="text-lg font-extrabold text-[var(--om-ink)]">{t('studio.delete_confirm_title')}</h3>
            <p className="mt-2 text-sm text-[var(--om-muted)]">{t('studio.delete_confirm_body')}</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="rounded-full border border-[var(--om-border)] bg-[var(--om-bg)] px-4 py-2 text-sm font-semibold text-[var(--om-ink)]"
                data-testid="studio-delete-cancel"
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                onClick={doDelete}
                className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white"
                data-testid="studio-delete-confirm"
              >
                {t('studio.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// Card + bandeau modération + widget activité 7j (Phase 10)          //
// ------------------------------------------------------------------ //
function StudioAppCard({ app, lang, t, onToggle, onDelete }) {
  const v = VERTICALS_BY_CODE[app.vertical];
  const color = v?.color || '#0A0E1A';
  const isPub = app.status === 'published';
  const flagged = !!app.is_flagged;
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getAppActivity(app.id);
      if (cancelled) return;
      if (res.ok) setActivity({ visits: res.visits || [], visits_7d: res.visits_7d || 0 });
    })();
    return () => { cancelled = true; };
  }, [app.id]);

  return (
    <div
      className={`flex flex-col rounded-2xl border p-5 ${
        flagged
          ? 'border-rose-400/40 bg-rose-500/5'
          : 'border-[var(--om-border)] bg-[var(--om-bg-pure)]'
      }`}
      data-testid={`studio-app-card-${app.id}`}
    >
      {flagged && (
        <div
          className="mb-4 flex items-start gap-2 rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-300"
          data-testid={`studio-app-flag-banner-${app.id}`}
        >
          <AlertTriangle size={12} strokeWidth={2.6} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-bold uppercase tracking-wider">{t('studio.moderation_title')}</div>
            <div className="mt-0.5 leading-relaxed text-rose-800/90 dark:text-rose-200/90">
              {app.moderation_reason || t('studio.moderation_no_reason')}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{ color, background: `${color}18` }}
        >
          {app.vertical}
        </span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
            isPub ? 'bg-emerald-500/15 text-emerald-700' : 'bg-[var(--om-ink)]/5 text-[var(--om-muted)]'
          }`}
          data-testid={`studio-app-status-${app.id}`}
        >
          {isPub ? <Globe size={9} strokeWidth={2.6} /> : <EyeOff size={9} strokeWidth={2.6} />}
          {isPub ? t('studio.status_published') : t('studio.status_draft')}
        </span>
        <span className="ml-auto text-[10px] tabular-nums text-[var(--om-muted-2)]">{formatDate(app.updated_at, lang)}</span>
      </div>

      <h3 className="mt-3 truncate text-base font-bold text-[var(--om-ink)]" data-testid={`studio-app-name-${app.id}`}>{app.name}</h3>
      {app.description && <p className="mt-1 line-clamp-2 text-sm text-[var(--om-muted)]">{app.description}</p>}
      <div className="mt-3 text-[11px] text-[var(--om-muted-2)]">
        /app/<span className="font-mono">{app.slug}</span> · {app.blocks?.length || 0} {t('studio.blocks')}
      </div>

      {/* Activité 7 jours */}
      <div className="mt-4 flex items-center gap-3 rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-2.5" data-testid={`studio-app-activity-${app.id}`}>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">{t('studio.activity_7d')}</div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0 text-[11px] leading-tight text-[var(--om-muted)]">
            <span>
              <Eye size={9} className="mr-0.5 inline align-[-1px]" strokeWidth={2.6} />
              <span className="font-bold tabular-nums text-[var(--om-ink)]" data-testid={`studio-app-visits-${app.id}`}>{activity ? activity.visits_7d : '—'}</span>{' '}{t('studio.visits')}
            </span>
            {app.submissions_count > 0 && <span><span className="font-bold tabular-nums text-[var(--om-ink)]">{app.submissions_count}</span> {t('studio.submissions')}</span>}
            {app.chat_messages_count > 0 && <span><span className="font-bold tabular-nums text-[var(--om-ink)]">{app.chat_messages_count}</span> {t('studio.chat_messages')}</span>}
          </div>
        </div>
        {activity && activity.visits.length > 0 && (
          <Sparkline data={activity.visits} accent={color} testid={`studio-app-sparkline-${app.id}`} />
        )}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Link
          to={`/studio/app/${app.id}`}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:shadow-sm"
          data-testid={`studio-app-edit-${app.id}`}
        >
          <Pencil size={11} strokeWidth={2.4} /> {t('studio.edit')}
        </Link>
        <button
          type="button"
          onClick={() => onToggle(app)}
          disabled={flagged && !isPub}
          className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          style={{ background: isPub ? '#8892A6' : color }}
          title={flagged && !isPub ? t('studio.moderation_blocked') : ''}
          data-testid={`studio-app-toggle-${app.id}`}
        >
          {isPub ? <EyeOff size={11} strokeWidth={2.4} /> : <Globe size={11} strokeWidth={2.4} />}
          {isPub ? t('studio.unpublish') : t('studio.publish')}
        </button>
        {isPub && (
          <a
            href={`/app/${app.slug}`}
            target="_blank"
            rel="noreferrer"
            className="grid h-7 w-7 place-items-center rounded-full text-[var(--om-muted)] hover:bg-[var(--om-bg)] hover:text-[var(--om-ink)]"
            title={t('studio.open_public')}
            data-testid={`studio-app-open-${app.id}`}
          >
            <ExternalLink size={12} strokeWidth={2.4} />
          </a>
        )}
        <button
          type="button"
          onClick={onDelete}
          className="grid h-7 w-7 place-items-center rounded-full text-rose-500 hover:bg-rose-500/10"
          title={t('studio.delete')}
          data-testid={`studio-app-delete-${app.id}`}
        >
          <Trash2 size={12} strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}
